import type { IndexDefinition } from '../../models/indexes';
import MultiKeyMap from '../../multiKeyMap';
import orderBy from '../../orderBy';
import { normalizeIndexDefinition } from './normalizeIndexDefinition';

export class DimensionTree {
  names = new Set<string>();
  nodes = new MultiKeyMap<readonly [string, string], DimensionTree>();

  static fromIndex(index: IndexDefinition): DimensionTree {
    index = normalizeIndexDefinition(index);

    const tree = new DimensionTree();
    const prefix: { key: string; dim: string }[] = Object.entries(index.spec.partialFilterExpression ?? {}).map(([key, expr]) => {
      return { key, dim: `=${JSON.stringify(expr)}` };
    });

    if (prefix.length > 0) {
      tree.add(index.spec.name ?? '', prefix);
    }

    for (const [key, order] of Object.entries(index.spec.key)) {
      prefix.push({ key, dim: String(order) });
      tree.add(index.spec.name ?? '', prefix);
    }

    return tree;
  }

  static mergeTrees(trees: Iterable<DimensionTree>): DimensionTree {
    const merged = new DimensionTree();
    for (const tree of trees) {
      merged.merge(tree);
    }
    return merged;
  }

  add(name: string, nodes: { key: string; dim: string }[]) {
    this._add(name, orderBy(nodes, ['key']));
  }

  _add(name: string, nodes: { key: string; dim: string }[]) {
    const [first, ...rest] = nodes;

    if (first) {
      const key = [first.key, first.dim] as const;

      let child = this.nodes.get(key);
      if (!child) {
        child = new DimensionTree();
        this.nodes.set(key, child);
      }

      child.add(name, rest);
    } else {
      this.names.add(name);
    }
  }

  merge(other: DimensionTree) {
    this.names = this.names.union(other.names);

    for (const [otherKey, otherChild] of other.nodes) {
      let child = this.nodes.get(otherKey);
      if (!child) {
        child = new DimensionTree();
        this.nodes.set(otherKey, child);
      }
      child.merge(otherChild);
    }
  }

  toString(indent = 0): string {
    let str = '';

    for (const [[key, value], child] of this.nodes) {
      str += ' '.repeat(indent) + key + ': ' + value;
      if (child.names.size > 0) {
        str += ' ' + JSON.stringify(Array.from(child.names));
      }
      str += '\n';
      str += child.toString(indent + 2);
    }
    return str;
  }

  covers(other: DimensionTree): Set<string> | undefined {
    if (other.names.size > 0 && this.names.size === 0) {
      return undefined;
    }

    const names = new Set(this.names);

    for (const [otherKey, otherChild] of other.nodes) {
      const possibleChildren = [this.nodes.get(otherKey)];

      if (otherKey[1].startsWith('=')) {
        possibleChildren.push(this.nodes.get([otherKey[0], '1']));
        possibleChildren.push(this.nodes.get([otherKey[0], '-1']));
      }

      if (possibleChildren.filter(Boolean).length === 0) {
        return undefined;
      }

      if (
        !possibleChildren.some((child) => {
          let result;
          if ((result = child?.covers(otherChild))) {
            for (const name of result) {
              names.add(name);
            }
            return true;
          }
          return false;
        })
      ) {
        return undefined;
      }
    }

    return names;
  }

  without(names: Iterable<string>): DimensionTree {
    const newTree = new DimensionTree();
    const nameSet = names instanceof Set ? names : new Set(names);
    newTree.names = this.names.difference(nameSet);

    for (const [key, child] of this.nodes) {
      const newChild = child.without(nameSet);
      if (newChild.names.size > 0 || newChild.nodes.size > 0) {
        newTree.nodes.set(key, newChild);
      }
    }

    return newTree;
  }
}
